import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type { UniqueProductFiltersInput } from '~/graphql/graphql';

export const useGetAvailableUniqueProductsWithSellers = (
  page: number,
  size: number,
  filters?: UniqueProductFiltersInput
) => {
  const uniqueProductsQuery = graphql(`
    query AllUniqueProducts(
      $page: Int!
      $size: Int!
      $filters: UniqueProductFiltersInput
    ) {
      findAvailableUniqueProductsWithSellers(
        pageInput: { page: $page, size: $size }
        filters: $filters
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          leadTimeMax
          leadTimeMin
          lengths {
            actualLengthInches
            nominalLengthInches
            displayValue
            id
          }
          listingsCount
          priceMax
          priceMin
          sellersCount
          totalStock {
            value
            unit
          }
          platformProduct {
            id
            name
            finishCode
            piecesPerBundle {
              value
              unit
            }
            unitsPerContainer {
              value
              unit
            }
            unitsPerRailCar {
              value
              unit
            }
            unitsPerTruckload {
              value
              unit
            }
            properties {
              species {
                code
                name
              }
              grade {
                code
                name
              }
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    uniqueProductsQuery,
    [
      'uniqueProductsWithSellers',
      page.toString(),
      size.toString(),
      JSON.stringify(filters ?? null),
    ],
    { page, size, filters }
  );
};

export const useUpdateCart = () => {
  const addToCartMutation = graphql(`
    mutation updateCart(
      $sellingOrganizationId: ID!
      $productId: ID!
      $quantity: QuantityInput!
      $locationId: ID
    ) {
      updateCart(
        sellingOrganizationId: $sellingOrganizationId
        productId: $productId
        quantity: $quantity
        locationId: $locationId
      ) {
        id
        items {
          quantity {
            value
            unit
          }
        }
      }
    }
  `);
  return useGraphqlMutation(addToCartMutation, ['cart', 'products']);
};

export const useGetCart = (locationId?: string) => {
  const query = graphql(`
    query GetCartsWithFilter($locationId: ID) {
      carts(filter: { locationId: $locationId }) {
        id
        sellingOrganization {
          id
          name
        }
        items {
          platformProductsLengths {
            id
            platformProduct {
              id
              name
              isNominal
              nominalThicknessInches
              nominalWidthInches
              actualThicknessInches
              actualWidthInches
              actualLengthInches
              standardUnitsCode
              unitsPerTruckload {
                value
                unit
              }
              unitsPerRailCar {
                value
                unit
              }
              piecesPerBundle {
                value
                unit
              }
              properties {
                grade {
                  code
                  name
                }
              }
            }
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
          }
          price
          quantity {
            value
            unit
          }
        }
        shippingLocation {
          id
          name
          region
          zipCode
          country
          city
          addressLine2
          addressLine1
        }
      }
    }
  `);
  return useGraphqlQuery(query, ['cart', locationId || ''], { locationId });
};

export const useGetCartById = (cartId: string) => {
  const query = graphql(`
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        sellingOrganization {
          id
          name
        }
        items {
          price
          quantity {
            value
            unit
          }
          platformProductsLengths {
            id
            platformProduct {
              id
              name
              isNominal
              nominalThicknessInches
              nominalWidthInches
              actualThicknessInches
              actualWidthInches
              actualLengthInches
              standardUnitsCode
              unitsPerTruckload {
                value
                unit
              }
              unitsPerRailCar {
                value
                unit
              }
              piecesPerBundle {
                value
                unit
              }
            }
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(query, ['cart', cartId], {
    cartId: cartId,
  });
};

export const useGetAvailableProducts = (page: number, size: number) => {
  const availableProductsQuery = graphql(`
    query AvailableProducts($page: Int!, $size: Int!) {
      availableProducts(pageInput: { page: $page, size: $size }) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          pricePerUnit
          organization {
            name
          }
          platformProductLength {
            platformProduct {
              name
              properties {
                species {
                  code
                }
                grade {
                  code
                }
              }
            }
            length {
              displayValue
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    availableProductsQuery,
    ['availableProducts', page.toString(), size.toString()],
    { page, size }
  );
};

export const useGetProductFilterOptions = () => {
  const query = graphql(`
    query ProductFilterOptions {
      lumberSpecies {
        code
        name
      }
      lumberGrades {
        code
        name
      }
      platformProductDimensions {
        nominalThicknessInches
        nominalWidthInches
      }
      allUniqueProductInventoryLocationsForBuyer {
        id
        name
        addressLine1
        addressLine2
        city
        region
        zipCode
        country
      }
    }
  `);

  return useGraphqlQuery(query, ['productFilterOptions']);
};

export const useGetSellerProduct = (sellerProductId: string) => {
  const sellerProductQuery = graphql(`
    query SellerProduct($id: ID!) {
      sellerProduct(id: $id) {
        id
        pricePerUnit
        minimumOrderQuantity {
          value
          unit
        }
        readyToShipDays
        unit
        organization {
          id
          name
        }
        platformProductLength {
          id
          platformProduct {
            name
            code
            isNominal
            actualThicknessInches
            actualWidthInches
            actualLengthInches
            nominalThicknessInches
            nominalWidthInches
            finishCode
            standardUnitsCode
            unitsPerContainer {
              value
              unit
            }
            unitsPerRailCar {
              value
              unit
            }
            unitsPerTruckload {
              value
              unit
            }
            piecesPerBundle {
              value
              unit
            }
            properties {
              species {
                code
                name
              }
              grade {
                code
                name
              }
              dryness {
                code
                name
              }
              surfacing {
                code
                name
              }
              treatment {
                code
                name
              }
              construction {
                code
                name
              }
            }
          }
          length {
            displayValue
            actualLengthInches
            nominalLengthInches
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    sellerProductQuery,
    ['sellerProduct', sellerProductId],
    {
      id: sellerProductId,
    }
  );
};
